// client/src/components/event/EventHubDashboard.jsx
//
// Per-event command center. One call to GET /:tenant/events/:eventId/hub
// (server/routes/eventRoutes.js) assembles tasks, ticket sales, check-in,
// budget vs actual, vendors, files and recent activity; each card links to
// the feature page that owns it.

import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { FaArrowLeft, FaHome } from 'react-icons/fa';
import AppFooter from '../AppFooter';
import DesktopActionBar from '../DesktopActionBar';
import EventLifecycleBadge from './EventLifecycleBadge';
import RecurrenceModal from './RecurrenceModal';
import GeneratePlanModal from '../campaign/GeneratePlanModal';
import PerformancePanel from '../analytics/PerformancePanel';
import { getSessionLocal } from '../../services/sessionLocal';
import { can } from '../../services/rolePermissions';

const baseUrl = process.env.REACT_APP_BASE_URL || 'http://localhost:5000';


/** Human label for a logged transaction: the type's own description
 * ("Event action created"), else the type name made readable. */
const activityLabel = (a) => a.type_label
  || String(a.type || '').toLowerCase().replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase());

export default function EventHubDashboard() {
  const { tenant, eventId } = useParams();
  const navigate = useNavigate();
  const { t: tr } = useTranslation();
  const [hub, setHub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [planning, setPlanning] = useState(false);
  const [recurrence, setRecurrence] = useState(false);
  const { role } = getSessionLocal();
  const canEdit = can(role, 'events', 'update');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${baseUrl}/api/events/${tenant}/events/${eventId}/hub`);
      setHub(data);
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to load event hub');
    } finally {
      setLoading(false);
    }
  }, [tenant, eventId]);

  useEffect(() => { load(); }, [load]);

  const navButtons = [
    { icon: <FaArrowLeft />, label: 'Back', onClick: () => navigate(-1) },
    { icon: <FaHome />, label: 'Home', onClick: () => navigate(`/${tenant}/dashboard`) },
  ];
  const footer = <AppFooter buttons={navButtons} />;
  const actionBar = <DesktopActionBar buttons={navButtons} />;

  if (loading) return <div className="p-6 text-sm opacity-70" style={{ paddingBottom: 72 }}>{actionBar}Loading…{footer}</div>;
  if (error) return <div className="p-6 text-sm text-red-600" style={{ paddingBottom: 72 }}>{actionBar}{error}{footer}</div>;
  if (!hub) return null;

  const go = (path) => () => navigate(`/${tenant}/${path}`);
  const money = (n) => `$${Number(n || 0).toFixed(2)}`;
  const t = hub.tasks;
  const tk = hub.tickets;
  const ci = hub.checkin;
  const b = hub.budget;
  const vendorCount = Object.values(hub.vendors || {}).reduce((s, n) => s + n, 0);
  const mk = hub.marketing || { campaigns: [], contentPipeline: {}, goals: {} };
  const cp = mk.contentPipeline || {};
  const primaryCampaign = (mk.campaigns || [])[0] || null;
  const goalKeys = Object.keys(mk.goals || {});

  return (
    <div className="max-w-4xl w-full mx-auto p-4 space-y-5 overflow-x-hidden" style={{ paddingBottom: 72 }}>
      {actionBar}
      <div>
        <div className="flex flex-wrap items-center gap-2">
          <h1 className="text-2xl font-semibold break-words min-w-0">{hub.event.name}</h1>
          <EventLifecycleBadge
            tenantId={tenant}
            eventId={eventId}
            status={hub.event.status}
            canEdit={canEdit}
            onChange={() => load()}
          />
        </div>
        <p className="text-sm opacity-60">
          {hub.event.startdate ? new Date(hub.event.startdate).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }) : 'No date set'}
          {' · '}{hub.event.item_count} item{hub.event.item_count === 1 ? '' : 's'}
        </p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 min-w-0">
        <Card title="Tasks" onClick={go('tasks')}>
          <Big>{t.open}</Big><Sub>open{t.overdue ? ` · ${t.overdue} overdue` : ''}</Sub>
          <Sub>{t.completed}/{t.total} done</Sub>
        </Card>

        <Card title="Ticket sales" onClick={go(`${eventId}/orders`)}>
          <Big>{tk.totalSold}</Big><Sub>sold · {money(tk.net)} net</Sub>
          {tk.refunded > 0 && <Sub>{money(tk.refunded)} refunded</Sub>}
        </Card>

        <Card title="Check-in" onClick={go(`${eventId}/checkin`)}>
          <Big>{ci.total ? Math.round((ci.checkedIn / ci.total) * 100) : 0}%</Big>
          <Sub>{ci.checkedIn}/{ci.total} checked in</Sub>
          {hub.waitlist.waiting > 0 && <Sub>{hub.waitlist.waiting} on waitlist</Sub>}
        </Card>

        <Card title="Budget" onClick={go(`${eventId}/budget`)}>
          <Big className={b.projectedNet >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}>
            {money(b.projectedNet)}
          </Big>
          <Sub>projected net</Sub>
          <Sub>{money(b.actualExpense)} spent / {money(b.estimatedExpense)} est.</Sub>
        </Card>

        <Card title="Vendors" onClick={go(`${eventId}/vendors`)}>
          <Big>{vendorCount}</Big>
          <Sub>{hub.vendors.booked || 0} booked · {hub.vendors.paid || 0} paid</Sub>
        </Card>

        <Card title="Analytics" onClick={go(`${eventId}/ticketing-analytics`)}>
          <Big>{tk.orderCount}</Big><Sub>orders · {money(tk.gross)} gross</Sub>
          <Sub>{money(tk.fees)} platform fees</Sub>
        </Card>

        <Card
          title="Marketing"
          onClick={primaryCampaign ? go(`campaigns/${primaryCampaign.id}`) : go('campaigns')}
        >
          <Big>{cp.published || 0}</Big>
          <Sub>posts published</Sub>
          <Sub>
            {(cp.idea || 0) + (cp.draft || 0)} idea · {cp.inReview || 0} review · {cp.approved || 0} ready
          </Sub>
        </Card>

        <Card title={tr('promoters.title', 'Promoters')} onClick={go(`${eventId}/promoters`)}>
          <Sub>{tr('promoters.hubCard', 'Leaderboard, links and rewards')}</Sub>
        </Card>

        <Card title={tr('forms.title', 'Forms')} onClick={go(`forms?event=${eventId}`)}>
          <Sub>{tr('forms.hubCard', 'Get notified, RSVP and volunteer sign-ups')}</Sub>
        </Card>

        <Card title={tr('postEvent.hubCard.title', 'After the event')} onClick={go(`${eventId}/after`)}>
          <Sub>{tr('postEvent.hubCard.sub', 'Thank-you email, survey, attendee photos and recap')}</Sub>
        </Card>
      </div>

      <div className="grid md:grid-cols-2 gap-4 min-w-0">
        <section className="min-w-0 rounded-lg border border-black/10 dark:border-white/10 p-4">
          <h3 className="font-semibold text-sm mb-2">Team</h3>
          {(hub.collaborators || []).length === 0 ? (
            <p className="text-sm opacity-60">No external collaborators.</p>
          ) : (
            <ul className="text-sm space-y-1">
              {hub.collaborators.map((c, i) => (
                <li key={`${c.companyname}-${c.role}-${i}`} className="flex justify-between gap-2 min-w-0">
                  <span className="min-w-0 break-words">{c.companyname}</span>
                  <span className="shrink-0 opacity-60">{c.role}</span>
                </li>
              ))}
            </ul>
          )}
          <div className="mt-2 text-xs opacity-60">{hub.files} file{hub.files === 1 ? '' : 's'} attached</div>
        </section>

        <section className="min-w-0 rounded-lg border border-black/10 dark:border-white/10 p-4">
          <h3 className="font-semibold text-sm mb-2">Recent activity</h3>
          {(hub.activity || []).length === 0 ? (
            <p className="text-sm opacity-60">Nothing logged yet.</p>
          ) : (
            <ul className="text-sm space-y-1">
              {hub.activity.map((a, i) => (
                <li key={`${a.created_on}-${i}`} className="flex justify-between gap-2 min-w-0">
                  <span className="min-w-0 truncate">{activityLabel(a)}{a.entity_name ? ` · ${a.entity_name}` : ''}</span>
                  <span className="shrink-0 opacity-50 whitespace-nowrap">{new Date(a.created_on).toLocaleDateString()}</span>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>

      {/* Marketing goals — KPI target vs actual (On-Screen 2.0) */}
      {(goalKeys.length > 0 || primaryCampaign) && (
        <section className="rounded-lg border border-black/10 p-4 dark:border-white/10">
          <div className="mb-2 flex items-center justify-between">
            <h3 className="text-sm font-semibold">
              Goals{primaryCampaign ? ` · ${primaryCampaign.name}` : ''}
            </h3>
            {primaryCampaign && (
              <button
                type="button"
                onClick={go(`campaigns/${primaryCampaign.id}`)}
                className="text-xs underline opacity-60"
              >
                Open campaign
              </button>
            )}
          </div>
          {goalKeys.length === 0 ? (
            <p className="text-sm opacity-60">
              No KPI targets set yet.{' '}
              {primaryCampaign ? 'Edit the campaign to add registration / revenue goals.' : ''}
            </p>
          ) : (
            <div className="space-y-2">
              {goalKeys.map((k) => {
                const g = mk.goals[k];
                const pct = g.pct == null ? 0 : Math.min(100, Math.max(0, g.pct));
                return (
                  <div key={k}>
                    <div className="flex justify-between gap-2 text-xs">
                      <span className="min-w-0 break-words capitalize">{k}</span>
                      <span className="shrink-0 opacity-70">
                        {g.actual == null ? '—' : g.actual} / {g.target}
                        {g.pct != null ? ` · ${g.pct}%` : ''}
                      </span>
                    </div>
                    <div className="mt-0.5 h-1.5 overflow-hidden rounded-full bg-black/10 dark:bg-white/10">
                      <div className="h-full rounded-full bg-emerald-500" style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>
      )}

      {/* Tracked-link clicks → conversions for this event (Growth Phase 1) */}
      <PerformancePanel tenantId={tenant} eventId={eventId} />

      <div className="flex flex-wrap gap-2 text-sm">
        <button type="button" onClick={go(`${eventId}/eventDetail`)} className="px-3 py-1.5 rounded border border-black/15 dark:border-white/15">Event details</button>
        <button type="button" onClick={go('calendar')} className="px-3 py-1.5 rounded border border-black/15 dark:border-white/15">Calendar feed</button>
        <button type="button" onClick={go(`${eventId}/public-page`)} className="px-3 py-1.5 rounded border border-black/15 dark:border-white/15">
          Public page
          {hub.event.public_visibility && hub.event.public_visibility !== 'private' && (
            <span className="ml-1 inline-block h-2 w-2 rounded-full bg-emerald-500 align-middle" title={`${hub.event.public_visibility} — /ev/…/${hub.event.public_slug}`} />
          )}
        </button>
        {primaryCampaign && (
          <button type="button" onClick={go(`campaigns/${primaryCampaign.id}/calendar`)} className="px-3 py-1.5 rounded border border-black/15 dark:border-white/15">Content calendar</button>
        )}
        {canEdit && (
          <button type="button" onClick={() => setRecurrence(true)} className="px-3 py-1.5 rounded border border-black/15 dark:border-white/15">
            {hub.event.series_id ? 'Manage series' : 'Make recurring'}
          </button>
        )}
        {canEdit && (
          <button type="button" onClick={() => setPlanning(true)} className="px-3 py-1.5 rounded bg-cobalt text-white">Generate campaign plan</button>
        )}
      </div>

      {planning && (
        <GeneratePlanModal
          tenantId={tenant}
          eventId={eventId}
          eventName={hub.event.name}
          onClose={() => setPlanning(false)}
          onDone={() => load()}
        />
      )}
      {recurrence && (
        <RecurrenceModal
          tenantId={tenant}
          eventId={eventId}
          onClose={() => setRecurrence(false)}
          onChanged={() => load()}
        />
      )}

      {footer}
    </div>
  );
}

function Card({ title, onClick, children }) {
  return (
    <button
      onClick={onClick}
      className="min-w-0 text-left rounded-lg border border-black/10 dark:border-white/10 p-3 hover:bg-black/5 dark:hover:bg-white/5"
    >
      <div className="text-xs uppercase tracking-wide opacity-60 mb-1 break-words">{title}</div>
      {children}
    </button>
  );
}
function Big({ children, className = '' }) {
  return <div className={`text-2xl font-semibold leading-tight break-words ${className}`}>{children}</div>;
}
function Sub({ children }) {
  return <div className="text-xs opacity-60 break-words">{children}</div>;
}
